import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom'; // Import useLocation
import { Paper, Typography, TextField, Button, CircularProgress, Link } from '@mui/material';
import customerAuthService from '@/service/auth.service/customer.auth.service';
import { useDispatch } from 'react-redux';
import { setAuthInfo } from '@/redux/auth/auth.slice';

export default function VerifyEmail() {
  const location = useLocation();
  const dispatch  =  useDispatch();
  const navigate = useNavigate();
  const { email } = location.state || {}; 

  

  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState(30);
  const [canResend, setCanResend] = useState(false);

  // Handle OTP input change
  const handleOtpChange = (e) => {
    const input = e.target.value;
    
    // Allow only digits and limit to 5 characters
    if (/^\d{0,5}$/.test(input)) {
      setOtp(input);
      setError('');
    } else {
      setError('OTP must be a 5-digit number');
    }
  };

  // Countdown timer logic
  useEffect(() => {
    let timer;
    if (!canResend && countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    } else if (countdown === 0) {
      setCanResend(true);
    }
    return () => clearTimeout(timer);
  }, [countdown, canResend]);

  // Handle OTP verification
  const handleOtpSubmit = async (otp) => {
    if (!otp) {
      setError('OTP cannot be empty');
      return;
    }
    setLoading(true);
    try {
      const response = await customerAuthService.verifyEmailOTP(email, otp);
      if (response?.status) {
        const { access_token, data:customer } = response.data;
      
        // 1. Set access token & role in localStorage
        localStorage.setItem('access_token', access_token);
        localStorage.setItem('role', 'customer');
      
        // 2. Set auth info in Redux
        dispatch(setAuthInfo({
          id: customer._id,
          role: 'customer',
          email: customer.email,
          first_name: customer.first_name,
          last_name: customer.last_name,
          profile_picture: customer.profile_picture,
        }));
      
        // 3. Navigate to upload profile
        setSuccessMessage('Email Verified Successfully!');
        navigate('/customer/onboarding/upload-profile-picture', {
          state: { email },
        });
      } else {
        setError('Invalid OTP');
      }
    } catch (error) {
      console.error('Error verifying OTP:', error);
      const message = error?.response?.data?.data?.error || 'An error occurred';   
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  // Handle Resend OTP
  const handleResendOtp = async () => {
    setLoading(true);
    try {
      await customerAuthService.resendEmailOTP(email);
      setCanResend(false);
      setCountdown(30);
    } catch (error) {
      setError('Failed to resend OTP');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper className="inner-card" elevation={5} sx={{ p: 3 }}>
      <Typography variant="h5" color="#5E5ACF" fontWeight="bold" gutterBottom>
        Enter OTP
      </Typography>

      <Typography className="textField" gutterBottom>
        A one-time password has been sent to:{' '}
        <Typography component="span" sx={{ fontWeight: 'bold', color: '#6a00f4', fontSize: '12px' }}>
          {email}
        </Typography>
      </Typography>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleOtpSubmit(otp);
        }}
        novalidate
      >
        <TextField
          fullWidth
          margin="normal"
          label="Enter 5-digit OTP"
          variant="outlined"
          value={otp}
          onChange={handleOtpChange}
          error={!!error}
          helperText={error}
        />

        {successMessage && (
          <Typography color="green" sx={{ fontSize: '0.875rem' }}>
            {successMessage}
          </Typography>
        )}

        <Button
          type="submit"
          variant="contained"
          fullWidth
          className="btn-submit"
          sx={{ mt: 2, color: 'black', fontWeight: 'bold' }}
        >
          Verify OTP
        </Button>
      </form>

      {/* Resend OTP Section */}
      {loading ? (
        <CircularProgress size={24} sx={{ color: 'black', mt: 2 }} />
      ) : (
        <>
          {!canResend ? (
            <Typography variant="body2" color="gray" sx={{ mt: 2 }}>
              You can resend OTP in {countdown} seconds
            </Typography>
          ) : (
            <Typography variant="body2" sx={{ mt: 2 }}>
              <Link
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  handleResendOtp();
                }}
                sx={{
                  cursor: 'pointer',
                  textDecoration: 'none',
                  color: '#5E5ACF',
                  fontWeight: 'bold',
                }}
              >
                Resend OTP
              </Link>
            </Typography>
          )}
        </>
      )}
    </Paper>
  );
}